import useEcommerceLearnMoreViewModel
    from '@experiences/ecommerce/src/lib/subcomponents/EcommerceLearnMoreComponent/EcommerceLearnMoreViewModel';
import {
    ActionCenterIcon,
    AppsIcon,
    StudioIcon,
} from '@experiences/images';
import { GlobalStyles } from '@experiences/theme';
import { UiText } from '@experiences/ui-common';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        productRow: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginBottom: '8px',
        },
        productTitle: {
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: theme.palette.semantic.colorForeground,
        },
        infoTooltipText: {
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundInverse,
            paddingLeft: '20px',
        },
        tooltipBox: {
            maxWidth: '490px',
            padding: '8px 12px',
        },
        productInfoIcon: {
            fontSize: '1.2em',
            marginLeft: '4px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        productSubtitleRow: {
            display: 'flex',
            flexDirection: 'row',
        },
        serviceIcon: { marginRight: '8px' },
        productSubtitle: {
            lineHeight: '20px',
            marginBottom: '2px',
            marginRight: '12px',
            fontWeight: 400,
            fontSize: '14px',
            color: theme.palette.semantic.colorForeground,
            display: 'inline',
        },
        moreServicesLink: {
            marginBottom: '2px',
            marginRight: '12px',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundLink,
            display: 'inline',
        },
        includedServiceItem: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '16px',
        },
        includedServicesTitle: {
            fontWeight: 400,
            lineHeight: '20px',
            fontSize: '14px',
            marginBottom: '13px',
            marginLeft: '11px',
            color: theme.palette.semantic.colorForeground,
        },
        includedServiceIcon: { marginBottom: '4px' },
        includedServiceText: {
            fontWeight: 400,
            fontSize: '10px',
            lineHeight: '16px',
            color: theme.palette.semantic.colorForeground,
        },
        includedServicesContainer: {
            padding: '8px 8px 0px 8px',
            width: '492px',
            height: '100%',
        },
        includedServicesPopover: { marginTop: '7px' },
    }),
}));

const EcommerceProductInfo: React.FC<{
    productCode: string;
    planType: string;
}> = ({
    productCode, planType,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();

    const {
        documentationLinksByProduct, getLearnMoreTooltipTranslationId,
    } = useEcommerceLearnMoreViewModel();

    return (
        <>
            <div className={classes.productRow}>
                <UiText className={classes.productTitle}>
                    {translate({ id: `CLIENT_PRODUCT_${productCode}` })}
                </UiText>
                <Tooltip
                    classes={{ tooltip: classes.tooltipBox }}
                    title={
                        <UiText data-cy="product-info-tooltip">
                            <ul className={classes.infoTooltipText}>
                                <FormattedMessage
                                    id={getLearnMoreTooltipTranslationId(productCode, planType)}
                                    values={{
                                        li: (chunk: React.ReactNode[]) => <li>
                                            {chunk}
                                        </li>,
                                        a: (msg: React.ReactNode[]) =>
                                            (
                                                <a
                                                    className={classes.a}
                                                    href={documentationLinksByProduct[productCode]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {msg}
                                                </a>
                                            ),
                                    }}
                                />
                            </ul>
                        </UiText>
                    }
                    role="tooltip"
                >
                    <InfoOutlined
                        className={classes.productInfoIcon}
                        data-cy={`${productCode}-info-icon`}
                        aria-hidden={false}
                        tabIndex={0}
                    />
                </Tooltip>
            </div>

            <div className={classes.productSubtitleRow}>
                {[ 'UNATTV2', 'UNATT', 'RU' ].includes(productCode) && (
                    <UiText className={classes.productSubtitle}>
                        {translate({ id: `CLIENT_PRODUCT_SUBTITLE_${productCode}_${planType}` })}
                    </UiText>
                )}

                {productCode === 'RPADEVPRONU' && (
                    <>
                        <StudioIcon className={classes.serviceIcon} />
                        <UiText className={classes.productSubtitle}>
                            {translate({ id: 'CLIENT_STUDIO' })}
                        </UiText>
                        <StudioIcon
                            className={classes.serviceIcon}
                            id={productCode} />
                        <UiText className={classes.productSubtitle}>
                            {translate({ id: 'CLIENT_STUDIO_X' })}
                        </UiText>
                    </>
                )}
                {productCode === 'CTZDEVNU' && (
                    <>
                        <StudioIcon
                            className={classes.serviceIcon}
                            id={productCode} />
                        <UiText className={classes.productSubtitle}>
                            {translate({ id: 'CLIENT_STUDIO_X' })}
                        </UiText>
                        <AppsIcon className={classes.serviceIcon} />
                        <UiText className={classes.productSubtitle}>
                            {translate({ id: 'CLIENT_APPS' })}
                        </UiText>
                    </>
                )}
                {productCode === 'ATTUNU' && (
                    <>
                        <AppsIcon className={classes.serviceIcon} />
                        <UiText className={classes.productSubtitle}>
                            {translate({ id: 'CLIENT_APPS' })}
                        </UiText>
                        <ActionCenterIcon className={classes.serviceIcon} />
                        <UiText className={classes.productSubtitle}>
                            {translate({ id: 'CLIENT_ACTIONS' })}
                        </UiText>
                    </>
                )}
            </div>
        </>
    );
};

export default EcommerceProductInfo;
